import { AddOrEditMatterRoleModal } from "@/components/features/settings/defaults/matter-roles/AddOrEditMatterRoleModal";
import { MatterRoleRowContextMenu } from "@/components/features/settings/defaults/matter-roles/MatterRoleRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { ActionConfirmationModal, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterRole } from "@/lib/data/react-query/matters/matter-role";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterRole } from "@/lib/types";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

const MATTER_ROLE_TABLE_COLUMN_IDS = ["Label", "Description"] as const;

const MATTER_ROLES_TABLE_LS_KEY: string = tanstackTableNames.matterRoles;

function MatterRolesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterRole,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterRole,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<MatterRole | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deleteMatterRole = useDeleteMatterRole();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY);
    const ids = [...MATTER_ROLE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const matterRoles = useMemo(
    () => matterSettingsData?.data.roles ?? [],
    [matterSettingsData?.data.roles],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matterRoles;
    return matterRoles.filter((row) => {
      const label = row.label.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      return label.includes(q) || description.includes(q);
    });
  }, [matterRoles, searchQuery]);

  const columns = useMemo<ColumnDef<MatterRole>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => row.original.label,
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => row.original.description ?? "-",
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_ROLES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const activeMatterRole = matterRoles.find((role) => role.id === activeRoleId);

  const handleDeleteMatterRole = async () => {
    if (!roleToDelete) return;

    await deleteMatterRole.mutateAsync({ id: roleToDelete.id });
    setRoleToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={MATTER_ROLE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search roles...",
        }}
        isLoading={isMatterSettingsLoading}
        isError={isMatterSettingsError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No matter roles found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="roles"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterRoles,
          "data-table-name": tanstackTableNames.matterRoles,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveRoleId(row.original.id);
            setIsEditing(true);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterRoleRowContextMenu
            role={row.original}
            trigger={trigger}
            canEdit={hasUpdatePermission}
            canDelete={hasDeletePermission}
            onEdit={() => {
              setActiveRoleId(row.original.id);
              setIsEditing(true);
            }}
            onDelete={() => {
              setRoleToDelete(row.original);
            }}
          />
        )}
      />

      {activeRoleId && activeMatterRole ? (
        <AddOrEditMatterRoleModal
          activeMatterRole={activeMatterRole}
          isReadOnly={!hasUpdatePermission}
          editing
          open={isEditing}
          setOpen={setIsEditing}
          setActiveMatterRoleId={setActiveRoleId}
        />
      ) : null}

      <AddOrEditMatterRoleModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
      />

      <ActionConfirmationModal
        open={Boolean(roleToDelete)}
        setOpen={(open) => {
          if (!open) setRoleToDelete(null);
        }}
        title="Delete matter role"
        description={`Are you sure you want to delete "${roleToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteMatterRole}
      />
    </div>
  );
}

export function MatterRolesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterRolesTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
